import { createContext, useContext, ReactNode, useRef, useEffect } from 'react';
import { useAccount, useClient } from 'wagmi';
import { useAbstractClient } from '@abstract-foundation/agw-react';
import {
  AGWAccountAbi,
  sessionKeyValidatorAddress,
} from '@abstract-foundation/agw-client/constants';
import { Address, concatHex, encodeAbiParameters, encodeFunctionData, Hash, keccak256, toFunctionSelector, toHex } from 'viem';
import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts';
import { encodeSession, LimitType, SessionClient, SessionConfig, SessionKeyValidatorAbi, ConstraintCondition, LimitUnlimited } from '@abstract-foundation/agw-client/sessions';
import { ABS_CHAIN, IS_TESTNET, COMMIT_REVEAL_CONTRACT_ADDRESS } from '~/core/constants';
import { WriteContractFunctionParameters } from 'viem/experimental';
import { readContract } from 'viem/actions';
import { useWriteContract } from '~/core/hooks/useWriteContract';
import { reservePopup, releaseReservedPopup } from '@privy-io/popup';
import { AsyncStorageByAddress } from '~/core/asyncStorages';

export type WriteContractFunctionParametersWithSessionKey = WriteContractFunctionParameters & { sessionKey: SESSION_KEYS }

interface AbsSessionContextType {
  writeContractAsync: (args: WriteContractFunctionParametersWithSessionKey) => Promise<`0x${string}`>;
  ensureSessionKey: (sessionKey: SESSION_KEYS) => Promise<void>;
}

const AbsSessionContext = createContext<AbsSessionContextType>({
  writeContractAsync: () => Promise.resolve() as any,
  ensureSessionKey: () => Promise.resolve(),
});

export const useAbsSession = () => useContext(AbsSessionContext);

interface AbsSessionProviderProps {
  children: ReactNode;
}

export const AbsSessionProvider = ({ children }: AbsSessionProviderProps) => {
  const { address } = useAccount();
  const { createSessionAsync: abstractCreateSessionAsync } = useCreateSession()
  const { data: agwClient } = useAbstractClient()
  const client = useClient()

  const refSessionConfigs = useRef<Record<SESSION_KEYS, SessionConfig>>({} as Record<SESSION_KEYS, SessionConfig>);
  const refSessionClients = useRef<Record<SESSION_KEYS, SessionClient>>({} as Record<SESSION_KEYS, SessionClient>);

  const refStorages = useRef<Record<SESSION_KEYS, AsyncStorageByAddress>>({} as Record<SESSION_KEYS, AsyncStorageByAddress>)
  const refConfigHashStorages = useRef<Record<SESSION_KEYS, AsyncStorageByAddress>>({} as Record<SESSION_KEYS, AsyncStorageByAddress>)
  const refIsSessionRevoked = useRef<Record<string, boolean>>({})

  useEffect(() => {
    if (!agwClient || !address) return
    const sessionKeys = Object.values(SESSION_KEYS).filter(x => typeof x === 'number') as SESSION_KEYS[]
    sessionKeys.forEach(async (sessionKey) => {
      if (sessionKey === SESSION_KEYS.NONE) return
      loadSession(sessionKey)
    })
  }, [agwClient, address])

  const getStorage = (sessionKey: SESSION_KEYS) => {
    if (!refStorages.current[sessionKey]) {
      refStorages.current[sessionKey] = new AsyncStorageByAddress(`abs-session-${sessionKey}`)
    }
    if (!refConfigHashStorages.current[sessionKey]) {
      refConfigHashStorages.current[sessionKey] = new AsyncStorageByAddress(`abs-session-hash-${sessionKey}`)
    }
    return {
      storage: refStorages.current[sessionKey],
      configHashStorage: refConfigHashStorages.current[sessionKey],
    }
  }

  const saveSession = (sessionConfig: ReturnType<typeof createSessionConfig>) => {
    const { storage, configHashStorage } = getStorage(sessionConfig.sessionKey)
    storage.set(address!, sessionConfig);
    configHashStorage.set(address!, getConfigHash(sessionConfig))
  }
  
  const loadSession = async (sessionKey: SESSION_KEYS) => {
    const { storage, configHashStorage } = getStorage(sessionKey)
    
    const storedSessionConfig = await storage.get(address!);
    if (!storedSessionConfig) 
      return null;

    const storedSessionConfigHash = await configHashStorage.get(address!);
    if (storedSessionConfigHash !== getConfigHash(storedSessionConfig)) {
      storage.remove(address!)
      return null;
    }

    const sessionClient = agwClient!.toSessionClient(privateKeyToAccount(storedSessionConfig.signerKey), storedSessionConfig.session);
    refSessionClients.current[sessionKey] = sessionClient
    refSessionConfigs.current[sessionKey] = storedSessionConfig.session
  }

  const clearSession = (sessionKey: SESSION_KEYS) => {
    delete refSessionClients.current[sessionKey];
    delete refSessionConfigs.current[sessionKey];
    delete refIsSessionRevoked.current[sessionKey];
    
    refStorages.current[sessionKey].remove(address!);
    refConfigHashStorages.current[sessionKey].remove(address!);
  }

  const isSessionRevoked = async (sessionConfig: SessionConfig) => {
    if (IS_TESTNET) return false      

    try {
      const hash = keccak256(encodeSession(sessionConfig))

      if (refIsSessionRevoked.current[hash]) {
        return refIsSessionRevoked.current[hash]
      }

      const status = await readContract(client!, {
        address: "0x34ca1501FAE231cC2ebc995CE013Dbe882d7d081",
        abi: [{"inputs":[{"internalType":"address","name":"account","type":"address"},{"internalType":"bytes32","name":"sessionHash","type":"bytes32"}],"name":"sessionStatus","outputs":[{"internalType":"enum SessionLib.Status","name":"","type":"uint8"}],"stateMutability":"view","type":"function"}],
        functionName: 'sessionStatus',
        args: [address!, hash],
      });
      const res = status !== 1
      refIsSessionRevoked.current[hash] = res
      return res
    } catch (error) {
      console.error(error)
      return false
    }
  }

  const isSessionExpired = (sessionConfig: SessionConfig) => {
    const adjustedExpiresAt = sessionConfig.expiresAt - 3600n // 1h early
    const now = Date.now() / 1000
    if (adjustedExpiresAt < now) {
      return true
    }
    return false
  }

  const createSessionAsync = async (sessionKey: SESSION_KEYS) => {
    if (!address) throw new Error('Wallet not connected')

    const sessionConfig = createSessionConfig(address, sessionKey);
    
    await abstractCreateSessionAsync(sessionConfig.session);

    const sessionClient = agwClient!.toSessionClient(privateKeyToAccount(sessionConfig.signerKey), sessionConfig.session);
    refSessionClients.current[sessionKey] = sessionClient
    refSessionConfigs.current[sessionKey] = sessionConfig.session
    saveSession(sessionConfig)
    return sessionClient
  }

  const createOrGetSessionClient = async (sessionKey: SESSION_KEYS) => {
    let sc: SessionClient | null = refSessionClients.current[sessionKey]
    if (sc && isSessionExpired(refSessionConfigs.current[sessionKey])) {
      sc = null
    }
    if (!sc) {
      sc = await createSessionAsync(sessionKey);
    }
    return sc;
  }

  const ensureSessionKey = async (sessionKey: SESSION_KEYS) => {
    if (sessionKey === SESSION_KEYS.NONE) return
    const hasSession = refSessionClients.current[sessionKey]
    if (!hasSession) {
      await createSessionAsync(sessionKey)
    }
  }

  const writeContractAsync = async (args: WriteContractFunctionParametersWithSessionKey) => {
    if (args.sessionKey === SESSION_KEYS.NONE) {
      throw new Error("Invalid action")
    }

    const sessionClient = await createOrGetSessionClient(args.sessionKey)

    const revoked = await isSessionRevoked(refSessionConfigs.current[args.sessionKey])
    if (revoked) {
      clearSession(args.sessionKey);
      throw new Error("Session is revoked, please try again")
    }

    // const gas = await estimateGas(client!, {
    //   account: address,
    //   to: args.address,
    //   data: encodeFunctionData(args),
    //   value: args.value ?? 0n,
    // })
    // // @ts-ignore
    // args.gas = gas * 3n

    const params = {
      abi: args.abi,
      account: sessionClient.account,
      chain: ABS_CHAIN,
      address: args.address,
      functionName: args.functionName,
      args: args.args,
      value: args.value ?? 0n,
      // // @ts-ignore
      // gas: args.gas,
    }

    return sessionClient.writeContract(params);
  }

  return (
    <AbsSessionContext.Provider value={{ writeContractAsync, ensureSessionKey }}>
      {children}
    </AbsSessionContext.Provider>
  );
};

const getConfigHash = (sessionConfig: ReturnType<typeof createSessionConfig>) => {
  return keccak256(toHex(sessionConfig.session.expiresAt.toString() + safeSerialize(sessionConfig.session.feeLimit) + sessionConfig.session.callPolicies.map(f => safeSerialize(f)).join('')))
}

// https://github.com/Abstract-Foundation/agw-sdk/blob/1da037cd6cbd8419000b3afe4b340f0f1bdff424/packages/agw-react/src/query/createSession.ts#L81
const useCreateSession = () => {
  const client = useClient()
  const { address } = useAccount()
  const { writeContractAsync } = useWriteContract()

  const createSessionAsync = async (session: SessionConfig) => {
    if (!client)
      throw new Error('Client not found')
    if (!address)
      throw new Error('Address not found')

    reservePopup()

    try {
      const validationHooks = await readContract(client, {
        address: address,
        abi: AGWAccountAbi,
        functionName: 'listHooks',
        args: [true],
      });
    
      const hasSessionModule = validationHooks.some(
        (hook) => hook === sessionKeyValidatorAddress,
      );
    
      let transactionHash: Hash | undefined = undefined;
    
      if (!hasSessionModule) {
        const encodedSession = encodeSession(session);
        transactionHash = await writeContractAsync({
          sessionKey: SESSION_KEYS.NONE,
          address: address,
          abi: AGWAccountAbi,
          functionName: 'addModule',
          args: [concatHex([sessionKeyValidatorAddress, encodedSession])],
        }, {
          skipReservePopup: true
        });
      } else {
        transactionHash = await writeContractAsync({
          sessionKey: SESSION_KEYS.NONE,
          address: sessionKeyValidatorAddress,
          abi: SessionKeyValidatorAbi,
          functionName: 'createSession',
          args: [session as never],
        }, {
          skipReservePopup: true
        });
      }
    
      return {
        transactionHash,
        session,
      };
    } catch (error) {
      throw error
    } finally {
      releaseReservedPopup()
    }
  }

  return { createSessionAsync }
}

export enum SESSION_KEYS {
  NONE,
  BLINKO,
}

const safeSerialize = (data: any) => JSON.stringify(data, (_, v) => typeof v === 'bigint' ? `bigint${v}` : v)

const MAX_LIMIT = 10000000000000000000000n // parseEther('10000')
const EXPIRES_AT = 60 * 60 * 24 * 365 // 1year
const FEE_LIMIT = {
  limitType: LimitType.Lifetime,
  limit: MAX_LIMIT,
  period: 0n,
}

const CALL_POLICIES = {
  [SESSION_KEYS.BLINKO]: (userAddress: Address) => [
    {
      address: COMMIT_REVEAL_CONTRACT_ADDRESS,
      function: "createGame((address,uint256,bytes32,bytes32,bytes32,address,uint256),bytes,bytes32)",
      limit: LimitUnlimited,
      maxValuePerUse: MAX_LIMIT,
      constraints: [],
    },
    {
      address: COMMIT_REVEAL_CONTRACT_ADDRESS,
      function: "cashOut(bytes32,uint256,bytes32,bytes32,uint256,bytes)",
      limit: LimitUnlimited,
      maxValuePerUse: 0n,
      constraints: [],
    },
    {
      address: COMMIT_REVEAL_CONTRACT_ADDRESS,
      function: "markGameAsLost(bytes32,bytes32,bytes32,uint256,bytes)",
      limit: LimitUnlimited,
      maxValuePerUse: 0n,
      constraints: [],
    },
  ]
}

const createSessionConfig = (userAddress: Address, sessionKey: SESSION_KEYS) => {
  const sessionPrivateKey = generatePrivateKey();
  const sessionSigner = privateKeyToAccount(sessionPrivateKey);

  const expiresAt = BigInt(Math.floor(Date.now() / 1000) + EXPIRES_AT)
  return {
    signerKey: sessionPrivateKey,
    sessionKey,
    session: {
      signer: sessionSigner.address,
      expiresAt,
      feeLimit: FEE_LIMIT,
      callPolicies: sessionKey === SESSION_KEYS.NONE 
        ? [] 
        : CALL_POLICIES[sessionKey](userAddress).map(c => ({
          target: c.address,
          selector: toFunctionSelector(c.function),
          valueLimit: c.limit,
          maxValuePerUse: c.maxValuePerUse,
          constraints: c.constraints,
        })),
      transferPolicies: [],
    }
  }
}
